import type { IQuotePrice } from '../types/Price'
import type { WorkData } from '../types/Work'
import type { WorkId } from '../types/WorkId'

export type PricingSelections = {
  name: string
  value: string
}

export type PricingItemsInput = {
  productKey: string
  productVersion: number
  correlationId: string
  quantity: number
  selections: PricingSelections[]
}

export type PricePresentationPriceProps = {
  totalListPrice: { taxed: number; untaxed: number }
  totalDiscountedPrice: { taxed: number; untaxed: number }
}

export const buildWebsitePricingQuoteRequest = (
  worksData: Record<WorkId, WorkData>,
  market: string,
  merchantId: string,
  couponCode: string | undefined,
  customerGroups: string[],
) => {
  const worksDataValues = Object.values(worksData).filter((workData) => workData.isDeleted !== true && workData.isDeleting !== true && workData.isValidQuantity)

  const itemGroups = worksDataValues.map((workData: WorkData, index: number) => {
    const groupCorrelationId = `group${index}`

    const otherItems = buildItemsFromWorkData(workData, groupCorrelationId)

    return {
      groupCorrelationId,
      items: otherItems,
    }
  })

  const filteredItemGroups = itemGroups.filter((itemGroup) => itemGroup.items.length > 0) as unknown as PricingItemsInput[]
  if (filteredItemGroups.length === 0) {
    return {}
  }

  return {
    excludeCartDiscounts: true,
    itemGroups: filteredItemGroups,
    couponCode,
    market,
    merchantId,
    customerGroups,
    billingAddress: {
      countryCode: market,
    },
    shippingAddress: {
      countryCode: market,
    },
  }
}

export const buildItemsFromWorkData = (workData: WorkData, groupCorrelationId: string): PricingItemsInput[] => {
  if (!workData.productInfo) {
    return []
  }

  if (typeof workData.inputQuantity === 'number') {
    // hard good
    return [
      {
        productKey: workData.productInfo.productKey,
        productVersion: workData.productInfo.productVersion,
        correlationId: `${groupCorrelationId}_1`,
        quantity: workData.inputQuantity,
        selections: mapOptionsToPricingSelections(workData.productInfo?.selections || {}),
      },
    ]
  } else {
    // sized good
    return Object.entries(workData.inputQuantity).map(([key, value]: [string, number], index: number) => {
      return {
        productKey: workData.productInfo!.productKey,
        productVersion: workData.productInfo!.productVersion,
        correlationId: `${groupCorrelationId}_${index}`,
        quantity: value,
        selections: mapOptionsToPricingSelections({
          ...workData.productInfo?.selections,
          Size: key,
        }),
      }
    })
  }
}

export const mapOptionsToPricingSelections = (options: Record<string, string>): PricingSelections[] => {
  const pricingSelections: PricingSelections[] = []
  Object.keys(options).forEach((optionKey: string) => {
    const optionValue = options[optionKey]
    if (optionValue !== undefined) {
      pricingSelections.push({
        name: optionKey,
        value: optionValue,
      })
    }
  })

  return pricingSelections
}

export const buildPricesForPricePresentation = (quotePrice: IQuotePrice): PricePresentationPriceProps => {
  if (!quotePrice?.itemGroups || quotePrice.itemGroups.length === 0) {
    return {
      totalListPrice: { taxed: 0, untaxed: 0 },
      totalDiscountedPrice: { taxed: 0, untaxed: 0 },
    }
  }

  const totalPriceObj = quotePrice.itemGroups.reduce(
    (totals, itemGroup) => {
      return {
        totalListPrice: {
          taxed: totals.totalListPrice.taxed + (itemGroup.totalListPriceCents?.taxed || 0),
          untaxed: totals.totalListPrice.untaxed + (itemGroup.totalListPriceCents?.untaxed || 0),
        },
        totalDiscountedPrice: {
          taxed: totals.totalDiscountedPrice.taxed + (itemGroup.totalDiscountedPriceCents?.taxed || 0),
          untaxed: totals.totalDiscountedPrice.untaxed + (itemGroup.totalDiscountedPriceCents?.untaxed || 0),
        },
      }
    },
    {
      totalListPrice: { taxed: 0, untaxed: 0 },
      totalDiscountedPrice: { taxed: 0, untaxed: 0 },
    },
  )

  return {
    totalListPrice: {
      taxed: totalPriceObj.totalListPrice.taxed / 100,
      untaxed: totalPriceObj.totalListPrice.untaxed / 100,
    },
    totalDiscountedPrice: {
      taxed: totalPriceObj.totalDiscountedPrice.taxed / 100,
      untaxed: totalPriceObj.totalDiscountedPrice.untaxed / 100,
    },
  }
}

export const getTotalQuantity = (quotePrice: IQuotePrice | undefined) => {
  if (!quotePrice || !quotePrice.itemGroups) {
    return 0
  }
  return quotePrice.itemGroups.reduce((acc, itemGroup) => {
    return acc + itemGroup.items.reduce((groupAcc, item) => groupAcc + item.quantity, 0)
  }, 0)
}
